import { LEVEL_LABEL, packetSeatLabel } from "@/ui/vocabulary";
import { RecordAfterFact } from "@/ui/RecordAfterFact";
import { useEffect, useMemo, useRef, useState } from "react";
import { packetListMine, packetOpen, packetPrint, panelAskHiringManager, panelMyAcknowledgments, panelDeclineRound, scorecardAppendAddendum, scorecardConfirmRecorded, scorecardOpen, scorecardRecordFor, scorecardSaveDraft, scorecardSubmit, scorecardRevise, type RatingInput, type ScorecardOpenOutput } from "@/api/tools";
import { FileField, DOCUMENT_TYPES } from "@/ui/FileField";
import { SavedPrint, type SavedRendition } from "@/ui/SavedPrint";
import { AppError, businessLocalNow, businessLocalToISO } from "@/api/runtime";
import { isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { replaceHash, useRoute } from "@/router";
import { useUnsavedGuard } from "@/ui/useUnsavedGuard";
import { useViewState } from "@/shell/viewState";
import { useSession } from "@/session/useSession";
import { Banner, Dialog, Empty, Field, KV, ObjectReadGuard, Pill, ReasonDialog, Receipt, Skeleton, Table, recordLabel, fmtDate, fmtTime } from "@/ui";
import { ASK_RECEIPT, INTERVIEW_STATE, INTERVIEW_STATE_LABEL, interviewState, ownRecommendationLabel, ownResult, scorecardStateLabel, type InterviewState } from "./packets/interviewState";
import { RescheduleReplaceDialog } from "./packets/RescheduleReplaceDialog";
import { AckControl } from "./panel/AckControl";
import { McScope } from "./panel/McScope";
import { RevokedPacket } from "./panel/RevokedPacket";
import { Button as McButton, ERROR_LABELS, recommendationWord, SCORECARD_LABELS } from "@/mc";
import { NotesRail, PlaybookScorecard } from "./b7/score/PlaybookScorecard";
import {
  dimensionLabel, guideFromPages, missingOf, offeredFromGuide, playbookOf, revisionDraft, skillTitlesOf, submitBody, withSaved,
  type Draft, type MissingItem,
} from "./b7/score/playbook";

// V1-INTERACTIONS §4.10.4–4.10.5 (My Interviews / My Packets), §4.11 (packet / scorecard). Routes: `#packets`,
// `#packets/<pk>` (the instrument), `#packets/<pk>/<cand>` (the scorecard for one candidate).

const VALUE_NAME: Record<string, string> = { respect: "Respect", ownership: "Ownership", opportunity: "Opportunity", together: "Together", service: "Service" };

type PacketListRow = Awaited<ReturnType<typeof packetListMine>>["items"][number];
/** One row per CANDIDATE interview (EM-11 / AF-12): the packet stays the seat's instrument, the row is the person. */
type InterviewRow = { key: string; packet: PacketListRow; interview: Record<string, unknown> | null; state: InterviewState };

function assessedLabel(values: readonly string[], skills: readonly number[]): string {
  return values.map((v) => VALUE_NAME[v] ?? v).join(", ") + (skills.length ? ` + skill ${skills.join(", ")}` : "");
}

function flattenInterviews(items: readonly PacketListRow[]): InterviewRow[] {
  return items.flatMap((packet): InterviewRow[] =>
    packet.interviews.length === 0
      ? [{ key: packet.packet_id, packet, interview: null, state: interviewState(null, packet.first_opened_at) }]
      : packet.interviews.map((interview) => ({
        key: `${packet.packet_id}:${String(interview["interview_id"])}`,
        packet,
        interview,
        state: interviewState(interview["scorecard_state"] as string | null, packet.first_opened_at),
      })),
  );
}

/** Level band first (LEVEL_LABEL's key order is the canonical order), then the role inside it — EM-2 / AF-2 (F1). */
function groupByBandThenRole(rows: readonly InterviewRow[]): [string, [string, InterviewRow[]][]][] {
  const known = Object.keys(LEVEL_LABEL);
  const bands = [...known, ...rows.map((r) => r.packet.level).filter((level) => !known.includes(level))];
  return [...new Set(bands)].flatMap((band) => {
    const inBand = rows.filter((row) => row.packet.level === band);
    if (!inBand.length) return [];
    const roles = [...new Set(inBand.map((row) => row.packet.requisition_id))].map(
      (rid) => [rid, inBand.filter((row) => row.packet.requisition_id === rid)] as [string, InterviewRow[]],
    );
    return [[band, roles] as [string, [string, InterviewRow[]][]]];
  });
}

/** The Packet cell IS the control (EM-12 / AF-13) and the one door into the packet + scorecard flow (EM-14 / AF-15). */
function PacketControl({ row }: { row: InterviewRow }) {
  const { packet: p, interview: i, state } = row;
  // One door (AF-15): the scorecard IS the focused flow once the round is scheduled or held — which is also when
  // `scorecard.open` resolves. Before that (a planned round, or no interview at all) the door is the instrument.
  const focused = !!i && (i["scorecard_id"] != null || ["scheduled", "held"].includes(String(i["status"])));
  const href = focused ? `#packets/${p.series_id}/${String(i!["application_id"])}` : `#packets/${p.series_id}`;
  const average = i?.["own_average"] == null ? null : String(i["own_average"]);
  const recommendation = ownRecommendationLabel(i?.["own_recommendation"]);
  const result = state === "submitted" ? ownResult(average, recommendation) : null;
  const retained = state === "submitted" ? scorecardStateLabel(i?.["scorecard_state"] as string | null) : null;
  return (
    <>
      {/* One status per row: a seat with its acknowledgment shows Declined in that pair (AckControl, below). */}
      {p.panel_seat_status === "declined" && !p.ack_id && <Pill tone="warn">Declined</Pill>}
      <a className="btn btn--sm btn--primary" href={href}>Open packet</a>
      <div className="packet-state">
        <Pill tone={state === "submitted" ? "ok" : state === "open" ? "warn" : "muted"}>{result ?? INTERVIEW_STATE_LABEL[state]}</Pill>
        {retained && retained !== "Submitted" && <span className="muted">{retained}</span>}
      </div>
    </>
  );
}

export function PacketsHome() {
  const { me } = useSession();
  const title = packetSeatLabel(me);
  const { register } = useViewState();
  const q = useRead(["packets"], () => packetListMine({}));
  const rows = useMemo(() => flattenInterviews(q.data?.items ?? []), [q.data]);
  const [filter, setFilter] = useState<InterviewState | "all">("all");
  useEffect(() => register({ view: "packets", ids: {}, summary: `my interviews ${rows.length}` }), [register, rows.length]);
  const counts = Object.fromEntries(INTERVIEW_STATE.map((state) => [state, rows.filter((row) => row.state === state).length])) as Record<InterviewState, number>;
  const shown = filter === "all" ? rows : rows.filter((row) => row.state === filter);
  const bands = groupByBandThenRole(shown);
  const head = (
    <tr>
      <th className="sticky-l">Candidate</th>
      <th>When</th>
      <th>Round</th>
      <th>Values &amp; Skills Assessed</th>
      <th>Panel</th>
      <th>Packet</th>
    </tr>
  );
  return (
    <div>
      <h1 className="page">{title}</h1>
      {/* EM-5 / R-INT-21: say what an interview IS here, not what the page is called. */}
      <p className="lede">An interview here is one round you hold with one candidate — the packet you prepare with, and the scorecard you submit afterwards.</p>
      {q.isLoading && <Skeleton />}
      {q.isError && <Banner error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && rows.length === 0 && <Empty level={2} heading="No packets yet">No packet is issued to you. Packets arrive when a round you hold is booked, or with the kickoff email.</Empty>}
      {q.data && rows.length > 0 && (
        <>
          <div className="row chips" role="group" aria-label="Filter by status">
            {(["all", ...INTERVIEW_STATE] as const).map((option) => (
              <button key={option} type="button" className={"chip" + (filter === option ? " chip--on" : "") + (option !== "all" && !counts[option] ? " chip--zero" : "")} aria-pressed={filter === option} onClick={() => setFilter(option)}>
                {option === "all" ? "All" : INTERVIEW_STATE_LABEL[option]}
                <span className="chip__n">{option === "all" ? rows.length : counts[option]}</span>
              </button>
            ))}
          </div>
          {bands.length === 0 && <Empty level={2} heading="Nothing in this status">No interview of yours is {INTERVIEW_STATE_LABEL[filter as InterviewState]?.toLowerCase()}. Choose All to see every interview.</Empty>}
          {bands.map(([band, roles]) => (
            <section key={band} aria-labelledby={`band-${band}`}>
              <h2 className="sec" id={`band-${band}`}>
                {LEVEL_LABEL[band] ?? band}
                <span className="sec__n">{roles.reduce((total, [, group]) => total + group.length, 0)}</span>
              </h2>
              {roles.map(([rid, group]) => {
                const label = `${rid} · ${group[0]!.packet.requisition_title}`;
                return (
                  <Table key={rid} label={label} caption={label} head={head}>
                    {group.map((row) => {
                      const { packet: p, interview: i } = row;
                      return (
                        <tr key={row.key}>
                          <td className="sticky-l cell--title">{i ? String(i["candidate"]) : <span className="muted">No candidate booked</span>}</td>
                          <td>{i ? (i["status"] === "held" ? `Held ${fmtDate(String(i["held_at"] ?? i["start_at"]), true)}` : i["start_at"] ? fmtDate(String(i["start_at"]), true) : "Not booked") : "Not booked"}</td>
                          <td><span className="nowrap">{p.round_label}</span> · {p.format} · <span className="nowrap">{p.duration_minutes} min</span></td>
                          <td>{assessedLabel(p.assesses, p.skill_numbers)}</td>
                          {/* EM-11 / AF-12: the panel is the other interviewers on this candidate's panel rounds (never the Recruiter's screen
                              or the Hiring Manager's round) — names only, never their scores. */}
                          <td>{((i?.["co_panelists"] as string[] | undefined) ?? []).length ? `You + ${(i!["co_panelists"] as string[]).join(", ")}` : <span className="muted">You only</span>}</td>
                          <td className="cell--packet">
                            <PacketControl row={row} />
                            {/* S13: the holder's own acknowledgment (CR-083), the design system's pair in its own .mc scope. */}
                            {p.ack_id ? (
                              <McScope inline>
                                <AckControl
                                  acknowledgmentId={p.ack_id}
                                  requisitionId={p.requisition_id}
                                  status={p.ack_status}
                                  nonresponse={p.nonresponse}
                                  roundLabel={p.round_label}
                                  interviewStart={i?.["start_at"] ? String(i["start_at"]) : null}
                                  changeStatus={p.change_status}
                                />
                              </McScope>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </Table>
                );
              })}
            </section>
          ))}
        </>
      )}
      {q.data && q.data.backups.length > 0 && (
        <section aria-labelledby="backup-rounds-heading">
          <h2 className="sec" id="backup-rounds-heading">Backup</h2>
          <p className="muted">You are the backup for these rounds. No packet is issued unless you are promoted to the primary seat.</p>
          <Table label="My backup rounds" head={<tr><th scope="col">Requisition</th><th scope="col">Round</th><th scope="col">Values &amp; Skills Assessed</th><th scope="col">Packet</th></tr>}>
            {q.data.backups.map((backup) => <tr key={backup.round_def_id}>
              {/* AF-1 closed the requisition door for this seat, so the requisition is named, never linked (N02). */}
              <td>{backup.requisition_id} · {backup.requisition_title}</td>
              <td>{backup.round_label} · {backup.format} · {backup.duration_minutes} min</td>
              <td>{assessedLabel(backup.assesses, backup.skill_numbers)}</td>
              <td>No packet — backup</td>
            </tr>)}
          </Table>
        </section>
      )}
    </div>
  );
}

function extractStatus(interview: Record<string, unknown>): string {
  return (interview["resume_extract"] as { status?: string } | null | undefined)?.status ?? "unavailable";
}

/** The worker prepares each interviewer's résumé context after issuance; poll the packet while any extract is pending. */
function packetPollWhilePending(data: unknown): number | false {
  const interviews = ((data as { interviews?: Record<string, unknown>[] } | undefined)?.interviews ?? []);
  return interviews.some((i) => extractStatus(i) === "pending") ? 5_000 : false;
}

// EM-7 / AF-6: the packet version is RETAINED — on the wire, in the Admin and Recruiter views (`PacketSet.tsx`) and
// on the printed rendition, where V1 §5.1 mandates it — but it is never printed on the screen the interviewer looks
// at. §3.2's identity is unchanged; only this seat's presentation drops the label (§4.11.1).
const showsVersion = (projection: string | undefined) => projection !== "own" && projection !== "author";

/** Render only the owning interviewer's server projection; no résumé download is granted here. */
function ResumeContext({ interview, packetVersion, projection }: { interview: Record<string, unknown> | undefined; packetVersion?: number | undefined; projection?: string | undefined }) {
  if (!interview || !("resume_extract" in interview)) return null;
  const extract = interview["resume_extract"] as { version?: number; lines?: string[]; status?: string } | null;
  const status = extractStatus(interview);
  return <section className="resume-context" aria-label="Résumé context">
    <h2 className="sec">Résumé context</h2>
    {extract?.version != null && showsVersion(projection) && <p className="muted">Pinned résumé v{extract.version}{packetVersion != null ? ` · packet v${packetVersion}` : ""}</p>}
    {status === "extracted" && extract?.lines?.length ? <ul>{extract.lines.map((line, index) => <li key={index}>{line}</li>)}</ul> : <p role={status === "pending" ? "status" : undefined}>{status === "pending" ? "Résumé context is pending." : status === "no_relevant_text" ? "No relevant résumé text was found for this assignment." : "Résumé context is unavailable."}</p>}
  </section>;
}

type PacketContext = Awaited<ReturnType<typeof packetOpen>>;
function AssignmentContext({ packet, application }: { packet: PacketContext; application?: string }) {
  const interview = packet.interviews.find(row => row["application_id"] === application);
  const skills = ((packet.pages?.["skill_pages"] ?? []) as { number: number; title: string }[]);
  const assigned = [...packet.values_assigned.map(value => VALUE_NAME[value] ?? value), ...packet.skill_numbers.map(number => skills.find(skill => skill.number === number)?.title ?? "—")];
  const guide = `${LEVEL_LABEL[packet.level] ?? packet.level} guide${showsVersion(packet.projection) ? ` v${packet.guide_version} · packet v${packet.version}` : ""}`;
  return <section className="card" aria-label="Assignment context">
    <h2 className="sec">Assignment</h2>
    <KV items={[["Requisition", packet.requisition_id], ["Title", packet.requisition_title], ["Guide", guide], ["Round", packet.round_label], ["Format", packet.format === "onsite" ? "Onsite" : packet.format === "virtual" ? "Virtual" : packet.format], ["Duration", `${packet.duration_minutes} min`], ...(interview ? [["Scheduled", interview["start_at"] ? fmtDate(String(interview["start_at"]), true) : "Not scheduled"]] as [string, string][] : [])]} />
    {/* EM-6 / AF-5: the interviewer's words for what this seat covers. */}
    <p>Values &amp; Skills Assessed: {assigned.join(" · ") || "No areas assigned"}</p>
  </section>;
}
/** The assignment as one header line on the scorecard: requisition and title, round, format, duration, when. */
function assignmentLine(packet: PacketContext, application: string): string {
  const interview = packet.interviews.find(row => row["application_id"] === application);
  const format = packet.format === "onsite" ? "Onsite" : packet.format === "virtual" ? "Virtual" : packet.format;
  const when = interview?.["start_at"] ? fmtDate(String(interview["start_at"]), true) : null;
  return [`${packet.requisition_id} ${packet.requisition_title}`, packet.round_label, format, `${packet.duration_minutes} min`, when].filter(Boolean).join(" · ");
}
const REMINDER_MECHANISMS = ["Use the questions in each assigned section below.", "Individual scorecards stay sealed to other interviewers until reveal.", "Submit scorecard records your assessment and clears your due item.", "Keep evidence focused on the role requirements.", "Use Ask the Hiring Manager or Ask your Recruiter to send a question from this packet."];
function KickoffReminders({ reminders }: { reminders: string[] }) {
  return reminders.length > 0 ? <details className="card"><summary>Before you start</summary><ol>{reminders.map((reminder, index) => <li key={reminder}><span>{reminder}</span><p className="muted">{REMINDER_MECHANISMS[index]}</p></li>)}</ol></details> : null;
}

/** S13: a packet an approved panel change revoked from this viewer shows the lock state with its date; any other
 * refused id stays not found (Law 15). The revoked list is the viewer's own (`panel.my_acknowledgments.revoked`). */
function RevokedOrMissing({ pk, query }: { pk: string; query: Parameters<typeof ObjectReadGuard>[0]["query"] }) {
  const mine = useRead(["my-acks"], () => panelMyAcknowledgments({}));
  if (mine.isLoading) return <Skeleton />;
  const revoked = mine.data?.revoked?.find((entry) => entry.series_id === pk);
  return revoked?.revoked_at ? <RevokedPacket revokedAt={revoked.revoked_at} /> : <ObjectReadGuard query={query} />;
}

export function PacketPage({ pk }: { pk: string }) {
  const route = useRoute();
  const action = new URLSearchParams(route.raw.split("?")[1]).get("action");
  const { register } = useViewState();
  const { can, me } = useSession();
  const listTitle = packetSeatLabel(me);
  const q = useRead(["packet", pk], () => packetOpen({ packet_id: pk }), { refetchInterval: (query) => packetPollWhilePending(query.state.data) });
  useEffect(() => register({ view: "packet", ids: { packet: pk }, summary: `packet ${pk}` }), [register, pk]);
  const inval = [["packet", pk], ["packets"], ["today"]] as const;
  const ask = useWrite((i: Parameters<typeof panelAskHiringManager>[0], k) => panelAskHiringManager(i, k), inval);
  const decline = useWrite((i: Parameters<typeof panelDeclineRound>[0], k) => panelDeclineRound(i, k), inval);
  const [dlg, setDlg] = useState<null | "ask" | "ask_recruiter" | "decline" | "print">(null);
  const [rendition, setRendition] = useState<SavedRendition | null>(null);
  const print = useWrite((input: Parameters<typeof packetPrint>[0]) => packetPrint(input));
  const [asked, setAsked] = useState<string | null>(null);
  const routedAction = useRef<string | null>(null);
  const closeAction = () => {
    setDlg(null);
    if (action) replaceHash(route.raw.split("?")[0]!);
  };
  useEffect(() => {
    if (!action) { routedAction.current = null; return; }
    if (!q.data || routedAction.current === action) return;
    routedAction.current = action;
    if (q.data.projection !== "own") return;
    if (action === "ask" && can("panel.ask_hiring_manager")) setDlg("ask");
    if (action === "ask_recruiter" && can("panel.ask_hiring_manager")) setDlg("ask_recruiter");
    if (action === "decline" && q.data.panel_seat_status !== "declined" && can("panel.decline_round")) setDlg("decline");
    if (action === "print" && can("packet.print")) void print.mutateAsync({ packet_id: pk, blank: true }).then(value => { setRendition(value); setDlg("print"); }).catch(() => undefined);
  }, [q.data, action]);
  if (q.error || !q.data) return isClientRefusal(q.error) ? <RevokedOrMissing pk={pk} query={q} /> : <ObjectReadGuard query={q} />;
  const p = q.data;
  const pages = p.pages ?? {};
  const header = (pages["header"] as Record<string, unknown> | undefined) ?? {};
  return (
    <div className="packet screen-only"><RecordAfterFact id={p.requisition_id} family="requisition" section="packets" /><p className="paper-only">Use Print / Save as PDF to print a saved packet with its export receipt.</p>
      <div className="hrow">
        <div>
          <h1 className="page">{p.projection === "own" ? `Your packet${p.interviews.length ? ` — ${p.interviews.map((i) => String(i["candidate"])).join(", ")}` : ""}` : `${p.round_label} packet — ${p.holder_name}`}</h1>
          <p className="lede">
            {p.projection === "own" && <><a href="#packets">← {listTitle}</a> · </>}{p.requisition_title} · {p.round_label} · {p.format} · {p.duration_minutes} min · {String(header["title"] ?? "Interview guide")}{showsVersion(p.projection) ? ` · guide v${p.guide_version} · packet v${p.version}` : ""} · holder {p.holder_name}
          </p>
        </div>
        <div className="acts">
          <Pill tone="muted">{decline.isSuccess || p.panel_seat_status === "declined" ? "Declined" : p.status}</Pill>
          {/* AF-7: Print stays — the interviewer takes the packet into the room on paper (CALL-17 11:39). */}
          {p.projection === "own" && can("packet.print") && (
            <button className="btn" disabled={print.isPending} onClick={() => void print.mutateAsync({ packet_id: pk, blank: true }).then((r) => { setRendition(r); setDlg("print"); }).catch(() => undefined)}>
              Print / Save as PDF
            </button>
          )}
          {p.projection === "own" && can("panel.ask_hiring_manager") && (
            <button className="btn" onClick={() => setDlg("ask")}>
              Ask the Hiring Manager
            </button>
          )}
          {/* EM-10 / AF-11: the same question channel, addressed to the Recruiter. */}
          {p.projection === "own" && can("panel.ask_hiring_manager") && (
            <button className="btn" onClick={() => setDlg("ask_recruiter")}>
              Ask your Recruiter
            </button>
          )}
          {p.projection === "own" && p.decline_notice && can("panel.decline_round") && !decline.isSuccess && p.panel_seat_status !== "declined" && p.status !== "voided" && p.round_kind !== "recruiter_screen" && p.round_kind !== "hm_round" && (
            <button className="btn" onClick={() => setDlg("decline")}>
              Reschedule / Replace
            </button>
          )}
        </div>
      </div>
      {decline.isSuccess || p.panel_seat_status === "declined" || p.status === "voided" ? <Receipt>You are no longer on this panel. The Recruiter and Hiring Manager have been told.</Receipt> : asked && <Receipt>{asked}</Receipt>}
      <Banner error={ask.error ?? decline.error} />
      <ReasonDialog title="Ask the Hiring Manager" label="Your question" confirm="Ask" open={dlg === "ask"} onClose={closeAction} busy={ask.isPending} error={ask.error} onConfirm={(question) => void ask.mutateAsync({ requisition_id: p.requisition_id, packet_id: p.series_id, question }).then(() => { closeAction(); setAsked(ASK_RECEIPT.hiring_manager); })} />
      <ReasonDialog title="Ask your Recruiter" label="Your question" confirm="Ask" open={dlg === "ask_recruiter"} onClose={closeAction} busy={ask.isPending} error={ask.error} onConfirm={(question) => void ask.mutateAsync({ requisition_id: p.requisition_id, packet_id: p.series_id, question, audience: "recruiter" }).then(() => { closeAction(); setAsked(ASK_RECEIPT.recruiter); })} />
      {p.decline_notice && <RescheduleReplaceDialog open={dlg === "decline"} notice={p.decline_notice} busy={decline.isPending} error={decline.error} onClose={closeAction} onConfirm={(input) => void decline.mutateAsync({ requisition_id: p.requisition_id, round_def_id: p.round_def_id, ...input }).then(closeAction)} />}
      <Banner error={print.error} />
      <SavedPrint title="Printable packet" value={dlg === "print" ? rendition : null} kind="packet" onClose={closeAction} />
      <AssignmentContext packet={p} />
      <KickoffReminders reminders={p.reminders} />
      <section className="card">
        <h2 className="sec">Interviews on this packet</h2>
        {p.interviews.length === 0 ? (
          <Empty heading="No interviews yet">No interview booked yet.</Empty>
        ) : (
          <ul className="plain">
            {p.interviews.map((i) => (
              <li key={String(i["interview_id"])}>
                <a href={`#packets/${p.series_id}/${String(i["application_id"])}`}>{String(i["candidate"])}</a> · <Pill category tone="muted">{String(i["status"])}</Pill>
                {/* R-INT-52: Eduardo's vocabulary, never the raw enum. */}
                {i["scorecard_state"] ? <Pill tone={String(i["scorecard_state"]) === "draft" ? "warn" : "ok"}>{scorecardStateLabel(i["scorecard_state"] as string)}</Pill> : null}
                <ResumeContext interview={i} packetVersion={p.version} projection={p.projection} />
              </li>
            ))}
          </ul>
        )}
      </section>
      {p.pages ? <GuidePages pages={p.pages} level={p.level} /> : <Empty level={2} heading="Packet pages are restricted">The pages are visible to the holder and the requisition's Recruiter and Hiring Manager.</Empty>}
    </div>
  );
}

// AF-4 / R-INT-140 (CALL-17 32:19): the standing guidance is NOT restated on every packet. What this screen keeps is
// what belongs to THIS round — the values assigned to the seat and the intake's own role-specific skill — and it points
// at the one information section for the rest. The packet's pinned `pages` are untouched on the wire and on paper
// (Law 3, §5.1); this is presentation only. The scorecard itself still carries each dimension's standard and its first
// approved question beside the rating control, because that is the instrument in use (§4.11.2), not a repetition.
function GuidePages({ pages, level }: { pages: Record<string, unknown>; level: string }) {
  const values = (pages["value_pages"] as Record<string, unknown>[] | undefined) ?? [];
  const skills = (pages["skill_pages"] as Record<string, unknown>[] | undefined) ?? [];
  return (
    <>
      {skills.map((s) => (
        <section key={String(s["number"])} className="card guide">
          {/* DC-246: a skill by its intake title, never a number */}
          <h2 className="sec">{String(s["title"] ?? "")}</h2>
          <p>
            <b>What strong performance looks like.</b> {String(s["what_strong_looks_like"])}
          </p>
        </section>
      ))}
      <section className="card guide">
        <h2 className="sec">Interview Guidance</h2>
        {values.length > 0 && <p>Your values on this round: {values.map((v) => String(v["name"])).join(" · ")}.</p>}
        <p>
          The values, the approved questions, the rating ladder and the recommendation routes for this level are on{" "}
          <a href={`#info/${level}`}>Interview Guidance</a>.
        </p>
      </section>
    </>
  );
}

// ---- the scorecard ----------------------------------------------------------------------------------------------------------------

type PaperMetadata = { source: "paper" | "verbal"; sourceFile: string | null; sourceRef: string; happened: string };
type LocalScorecard = { paperMetadata?: PaperMetadata; draft: Draft; expectedVersion: string | null; savedAt: string; mode: "draft" | "revision" | "paper"; revisionNo: number };

function emptyDraft(dims: Record<string, unknown>[]): Draft {
  return { ratings: dims.map((d) => ({ dimension_type: d["type"] as "value", dimension_key: String(d["key"]), state: "unanswered" })), coc_answer: null, attested: false };
}

function ratingForDimension(dim: Record<string, unknown>, ratings: RatingInput[]) {
  return ratings.find((row) => row.dimension_type === String(dim["type"]) && row.dimension_key === String(dim["key"]));
}

/** The instrument's keys own evidence identity; API row order is never meaningful to the editor. */
function mergeRatings(dims: Record<string, unknown>[], ratings: RatingInput[] | null | undefined): RatingInput[] {
  return emptyDraft(dims).ratings.map((row, i) => ratingForDimension(dims[i]!, ratings ?? []) ?? row);
}

function DraftComparison({ local, remote, dimensions, skillTitles }: { local: Draft; remote: Draft; dimensions: Record<string, unknown>[]; skillTitles: Readonly<Record<string, string>> }) {
  const rows: { field: string; local: unknown; remote: unknown }[] = [];
  const add = (field: string, mine: unknown, theirs: unknown) => {
    if (JSON.stringify(mine ?? null) !== JSON.stringify(theirs ?? null)) rows.push({ field, local: mine, remote: theirs });
  };
  for (const key of new Set([...Object.keys(local), ...Object.keys(remote)])) {
    if (key !== "ratings") add(recordLabel(key), local[key as keyof Draft], remote[key as keyof Draft]);
  }
  for (const dimension of dimensions) {
    const mine = ratingForDimension(dimension, local.ratings) ?? {}, theirs = ratingForDimension(dimension, remote.ratings) ?? {};
    const name = dimensionLabel({ type: dimension["type"] === "skill" ? "skill" : "value", key: String(dimension["key"]) }, skillTitles) ?? String(dimension["key"]);
    for (const key of new Set([...Object.keys(mine), ...Object.keys(theirs)])) {
      if (key !== "dimension_type" && key !== "dimension_key") add(`${name} · ${recordLabel(key)}`, (mine as Record<string, unknown>)[key], (theirs as Record<string, unknown>)[key]);
    }
  }
  const value = (item: unknown) => item == null || item === "" ? "Not entered" : typeof item === "boolean" ? item ? "Yes" : "No" : recommendationWord(String(item))?.label ?? (item === "not_assessed" ? SCORECARD_LABELS.notAssessed : String(item));
  return rows.length ? <Table label="Draft differences" head={<tr><th>Field</th><th>Your changes</th><th>Saved elsewhere</th></tr>}>{rows.map(row => <tr key={row.field}><th scope="row">{row.field}</th><td>{value(row.local)}</td><td>{value(row.remote)}</td></tr>)}</Table> : <p>Both drafts contain the same field values.</p>;
}

export function ScorecardPage({ pk, cand }: { pk: string; cand: string }) {
  const { me, can } = useSession();
  const { register } = useViewState();
  const q = useRead(["scorecard", pk, cand], () => scorecardOpen({ packet_id: pk, application_id: cand }));
  const packet = useRead(["packet", pk], () => packetOpen({ packet_id: pk }), { enabled: !!q.data && !q.error, refetchInterval: (query) => packetPollWhilePending(query.state.data) });
  useEffect(() => register({ view: "scorecard", ids: { packet: pk, application: cand }, summary: `scorecard ${q.data?.status ?? ""}` }), [register, pk, cand, q.data?.status]);
  const inval = [["scorecard", pk, cand], ["packet", pk], ["packets"], ["round"], ["today"]] as const;
  const save = useWrite((i: Parameters<typeof scorecardSaveDraft>[0], k) => scorecardSaveDraft(i, k), inval);
  const submit = useWrite((i: Parameters<typeof scorecardSubmit>[0], k) => scorecardSubmit(i, k), inval);
  const recordFor = useWrite((i: Parameters<typeof scorecardRecordFor>[0], k) => scorecardRecordFor(i, k), inval);
  const confirm = useWrite((i: { scorecard_id: string }, k) => scorecardConfirmRecorded(i, k), inval);
  const addendum = useWrite((i: { scorecard_id: string; text: string; reason: string }, k) => scorecardAppendAddendum(i, k), inval);
  const [paper, setPaper] = useState(() => new URLSearchParams(location.hash.split("?")[1]).get("record") === "paper");
  const [paperDlg, setPaperDlg] = useState(false);
  const [source, setSource] = useState<"paper" | "verbal">("paper");
  const [sourceFile, setSourceFile] = useState<string | null>(null);
  const [sourceBusy, setSourceBusy] = useState(false);
  const [printed, setPrinted] = useState<SavedRendition | null>(null);
  const print = useWrite((input: Parameters<typeof packetPrint>[0]) => packetPrint(input));
  const [sourceRef, setSourceRef] = useState("");
  const [happened, setHappened] = useState("");
  const paperMetadata = useRef<PaperMetadata>({ source, sourceFile, sourceRef, happened });
  paperMetadata.current = { source, sourceFile, sourceRef, happened };
  const revise = useWrite(scorecardRevise, inval);
  const ask = useWrite((input: Parameters<typeof panelAskHiringManager>[0], key) => panelAskHiringManager(input, key), [["today"]]);
  const [askDialog, setAskDialog] = useState(false);
  const [askRecruiterDialog, setAskRecruiterDialog] = useState(false);
  const [declineDialog, setDeclineDialog] = useState(false);
  // AF-13 / R-INT-25: the row's side buttons are gone, so the actions live INSIDE the focused flow the Packet cell
  // opens — Print, Ask the Hiring Manager, Ask your Recruiter and Reschedule / Replace are all reachable here.
  const decline = useWrite((input: Parameters<typeof panelDeclineRound>[0], key) => panelDeclineRound(input, key), [["packet", pk], ["packets"], ["today"]]);
  const [asked, setAsked] = useState<string | null>(null);
  const [revising, setRevising] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revisionDialog, setRevisionDialog] = useState(false);
  const [addDlg, setAddDlg] = useState(false);
  const [addText, setAddText] = useState("");
  const [d, setD] = useState<Draft | null>(null);
  const [version, setVersion] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "retrying" | "failed" | "conflict">("idle");
  const [comparing, setComparing] = useState(false);
  const otherDraft = useRead(["scorecard-comparison", pk, cand], () => scorecardOpen({ packet_id: pk, application_id: cand }), { enabled: comparing && status === "conflict", staleTime: 0 });
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [savedFields, setSavedFields] = useState<Record<string, unknown> | null>(null);
  const timer = useRef<number | null>(null);
  const retryTimer = useRef<number | null>(null);
  const inflight = useRef<Promise<void> | null>(null);
  const saveGeneration = useRef(0);
  const retryStarted = useRef<number | null>(null);
  const retryDelay = useRef(2000);
  const autoSaveStopped = useRef(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [recovery, setRecovery] = useState<LocalScorecard | null>(null);
  const recovered = useRef(false);
  const sc = useMemo(() => q.data && me?.view_as ? { ...q.data, can: Object.fromEntries(Object.keys(q.data.can).map(k => [k, false])) } : q.data, [q.data, me?.view_as]);
  const canDraft = !!sc?.can["save_draft"] && sc.projection === "author";
  const canEdit = canDraft || (revising && !!sc?.can["revise"]) || (paper && !!sc?.can["record_for"]);
  const editable = canEdit && !submitting && !submit.isPending && !revise.isPending;
  const autosaves = canDraft && !paper && !revising && !submitting;
  // The scorecard (or unmaterialized interview occurrence) binds the backend's pinned instrument,
  // independently of a newer packet version the series route may currently resolve to.
  const localKey = `ta.scorecard.${me?.person_id}.${sc?.author_id}.${sc?.scorecard_id ?? sc?.interview_id}.${sc?.current_revision_no}.${encodeURIComponent(JSON.stringify(sc?.dimension_set))}`;
  const latest = useRef({ d, version, status, autosaves, revising, paper, sc, localKey });
  latest.current = { d, version, status, autosaves, revising, paper, sc, localKey };
  const stopTimers = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    timer.current = retryTimer.current = null;
  };
  const clearLocal = () => {
    saveGeneration.current++;
    stopTimers();
    dirtyRef.current = false;
    setDirty(false);
    setRecovery(null);
    retryStarted.current = null;
    retryDelay.current = 2000;
    autoSaveStopped.current = false;
    try { sessionStorage.removeItem(localKey); } catch { /* Keep the live form when storage is unavailable. */ }
  };
  const remember = (next: Draft) => {
    const state = latest.current;
    const local: LocalScorecard = { ...(state.paper ? { paperMetadata: paperMetadata.current } : {}), draft: next, expectedVersion: state.version, savedAt: new Date().toISOString(), mode: state.revising ? "revision" : state.paper ? "paper" : "draft", revisionNo: state.sc?.current_revision_no ?? 0 };
    try { sessionStorage.setItem(state.localKey, JSON.stringify(local)); } catch { /* The departure guard still protects the live form. */ }
  };
  const guard = useUnsavedGuard(dirty, revising ? "this scorecard revision" : paper ? "this paper scorecard" : "this scorecard", clearLocal);
  useEffect(() => {
    if (!sc || d) return;
    const base = emptyDraft(sc.dimension_set);
    const server = sc.draft as Draft | null;
    setD(server ? { ...base, ...server, ratings: mergeRatings(sc.dimension_set, server.ratings) } : base);
    setVersion(sc.draft_version);
    if (recovered.current) return;
    recovered.current = true;
    try {
      const saved = sessionStorage.getItem(localKey);
      if (!saved) return;
      const local = JSON.parse(saved) as LocalScorecard;
      const allowed = local.mode === "revision" ? !!sc.can["revise"] && local.revisionNo === sc.current_revision_no : local.mode === "paper" ? !!sc.can["record_for"] : canDraft;
      const newer = local.mode !== "draft" || !sc.draft_saved_at || new Date(local.savedAt) > new Date(sc.draft_saved_at);
      if (allowed && newer && local.draft && Array.isArray(local.draft.ratings) && local.savedAt) setRecovery(local);
      else sessionStorage.removeItem(localKey);
    } catch { /* A malformed/stale copy cannot replace the server's assessment. */ }
    // Initialization is bound to the object. Subsequent refetches never overwrite the active editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sc]);
  const flush = (next?: Draft, expected?: string | null): Promise<void> => {
    const state = latest.current;
    if (next !== undefined) { autoSaveStopped.current = false; retryStarted.current = null; retryDelay.current = 2000; }
    if (autoSaveStopped.current) return Promise.resolve();
    if (retryStarted.current !== null && Date.now() - retryStarted.current >= 60_000) {
      autoSaveStopped.current = true; setStatus("failed"); return Promise.resolve();
    }
    if (!state.autosaves || !dirtyRef.current || !state.d || (state.status === "conflict" && expected === undefined)) return Promise.resolve();
    if (inflight.current) return inflight.current;
    stopTimers();
    const snapshot = next ?? state.d;
    const snapshotJSON = JSON.stringify(snapshot);
    const snapshotKey = state.localKey;
    const generation = saveGeneration.current;
    setStatus("saving");
    const pending = save.mutateAsync({ packet_id: pk, application_id: cand, draft: snapshot, expected_version: expected === undefined ? state.version : expected })
      .then((res) => {
        if (generation !== saveGeneration.current) return;
        latest.current.version = res.draft_version;
        setVersion(res.draft_version);
        setSavedAt(res.draft_saved_at);
        setSavedFields(res as unknown as Record<string, unknown>);
        retryStarted.current = null;
        retryDelay.current = 2000;
        if (snapshotJSON === JSON.stringify(latest.current.d)) {
          dirtyRef.current = false;
          setDirty(false);
          setStatus("saved");
          try { sessionStorage.removeItem(snapshotKey); } catch { /* Storage unavailable. */ }
        } else {
          setStatus("saving");
          remember(latest.current.d!);
          timer.current = window.setTimeout(() => void flush(), 0);
        }
      })
      .catch((error: unknown) => {
        if (generation !== saveGeneration.current) return;
        const conflict = error instanceof AppError && error.status === 409 && Object.hasOwn(error.fieldErrors, "expected_version");
        setStatus(conflict ? "conflict" : "failed");
        if (latest.current.d) remember(latest.current.d);
        const retryable = !(error instanceof AppError) || error.unreachable || error.status >= 500;
        if (!conflict && retryable) {
          retryStarted.current ??= Date.now();
          const remaining = 60_000 - (Date.now() - retryStarted.current);
          if (remaining > 0) {
            setStatus("retrying");
            retryTimer.current = window.setTimeout(() => void flush(), Math.min(retryDelay.current, remaining));
            retryDelay.current = Math.min(retryDelay.current * 2, 30_000);
          } else autoSaveStopped.current = true;
        } else autoSaveStopped.current = true;
      }).finally(() => { inflight.current = null; });
    inflight.current = pending;
    return pending;
  };
  const flushLatest = useRef(flush);
  flushLatest.current = flush;
  useEffect(() => {
    const interval = window.setInterval(() => { if (dirtyRef.current) void flushLatest.current(); }, 30_000);
    return () => { saveGeneration.current++; window.clearInterval(interval); stopTimers(); };
  }, []);
  const change = (patch: Partial<Draft>) => {
    const current = latest.current.d;
    if (!current) return;
    const next = { ...current, ...patch };
    latest.current.d = next;
    setD(next);
    dirtyRef.current = true;
    setDirty(true);
    remember(next);
    if (!autosaves) return;
    autoSaveStopped.current = false; retryStarted.current = null; retryDelay.current = 2000;
    setStatus("saving");
    stopTimers();
    timer.current = window.setTimeout(() => void flushLatest.current(), 2000);
  };
  const changePaper = (patch: Partial<PaperMetadata>) => {
    paperMetadata.current = { ...paperMetadata.current, ...patch };
    const value = paperMetadata.current;
    setSource(value.source); setSourceFile(value.sourceFile); setSourceRef(value.sourceRef); setHappened(value.happened);
    change({});
  };
  // B14 (brief §2.2): the frame both scorecard routes mount. A projection whose `scorecard.open` carries no guide (the
  // Recruiter compiling or keying a paper card) reads the same pinned pages from `packet.open`; with no level block the
  // offered options are the guide's own.
  const playbookOpen = useMemo((): ScorecardOpenOutput | null => {
    if (!sc) return null;
    const raw = sc as unknown as Record<string, unknown>;
    const dims = sc.dimension_set.map((dim) => ({ type: String(dim["type"]), key: String(dim["key"]) }));
    const guide = raw["guide"] ?? guideFromPages(packet.data?.pages, packet.data?.requisition_title ?? null, dims);
    const level = raw["level"] ?? { offered: offeredFromGuide(playbookOf({ guide }).guide) };
    return withSaved({ ...sc, guide, level } as unknown as ScorecardOpenOutput, savedFields);
  }, [sc, packet.data, savedFields]);
  const skillTitles = skillTitlesOf(playbookOpen ? playbookOf(playbookOpen).guide : null);
  if (q.error || !sc) return <ObjectReadGuard query={q} />;
  const readonlyRev = sc.revision;
  const rows = sc.ratings;
  const editingFrame = !!d && (sc.projection === "author" || sc.projection === "compile" || sc.projection === "admin") && (sc.status === "draft" || revising);
  const frameMode = paper ? "paper" : revising ? "revision" : editingFrame && sc.projection === "author" ? "draft" : "readonly";
  const frameDraft: Draft | null = editingFrame ? d : readonlyRev ? revisionDraft(playbookDims(sc), readonlyRev, rows) : null;
  const frameMissing: MissingItem[] = frameMode === "draft" && playbookOpen ? missingOf((playbookOpen as unknown as Record<string, unknown>)["missing"]) : [];
  const frameError = paper ? recordFor.error : revising ? revise.error : submit.error;
  const fieldErrors = frameError instanceof AppError ? { ...frameError.fieldErrors } : {};
  const onFrameSubmit = () => {
    if (!d) return;
    if (paper) { setPaperDlg(true); return; }
    stopTimers();
    if (revising) { setRevisionDialog(true); return; }
    setSubmitting(true);
    latest.current.autosaves = false;
    void (inflight.current ?? Promise.resolve()).then(() => submit.mutateAsync({ packet_id: pk, application_id: cand, draft: submitBody(d, "draft") })).then(clearLocal).catch(() => undefined).finally(() => setSubmitting(false));
  };
  // the save line keeps V1's words and its `.save-status` hook (the walks read it); a failed autosave keeps its Retry
  const saveState = paper ? <span className="mc-hint save-status" role="status" aria-live="polite">Paper scorecard not saved until you record it</span>
    : revising ? <span className="mc-hint save-status" role="status" aria-live="polite">Revision not saved until you submit it</span>
      : autosaves ? (
        <span className="mc-hint save-status" role="status" aria-live="polite">
          {status === "saving" ? "Saving…" : status === "retrying" ? "Not saved — retrying" : status === "failed" ? "Failed to save — Retry" : status === "conflict" ? "Conflict" : (savedAt ?? sc.draft_saved_at) ? `Saved ${fmtTime(savedAt ?? sc.draft_saved_at)}` : "Not saved yet"}
          {status === "failed" && d ? <> <McButton sm onClick={() => { void flush(d, version); }}>{ERROR_LABELS.retry}</McButton></> : null}
        </span>
      ) : null;
  return (
    <div className="scorecard screen-only" onBlurCapture={(event) => { if (event.target.matches("input,textarea,select")) void flushLatest.current(); }}>{guard.dialog}<RecordAfterFact id={sc.requisition_id} family="requisition" section="packets" references={{ interview_id: sc.interview_id }} /><p className="paper-only">Use Print saved scorecard to print the stored assessment with its export receipt.</p>
      <div className="hrow">
        <div>
          <h1 className="page">{sc.projection === "author" ? "Your packet" : `${sc.author_name}'s packet`} — {sc.candidate}</h1>
          <p className="lede" id="round-status" tabIndex={-1}>
            <a href={`#packets/${pk}`}>← packet</a>{packet.data ? <> · {assignmentLine(packet.data, cand)}</> : <> · {sc.round_label}</>} · author {sc.author_name} · {sc.interview_status === "held" ? `held ${fmtDate(sc.held_at, true)}` : sc.interview_status}
          </p>
        </div>
        <div className="acts">
          {/* R-INT-52: Eduardo's vocabulary for the retained record, never the raw database enum. */}
          <Pill tone={sc.status === "draft" ? "warn" : "ok"}>{sc.status === "draft" ? "Draft" : sc.status === "submitted" ? `Submitted ${fmtDate(sc.submitted_at)}${sc.current_revision_no > 1 ? ` · revised ${sc.current_revision_no - 1}×` : ""}` : scorecardStateLabel(sc.status)}</Pill>
          {sc.source !== "app" && <Pill tone={sc.confirmed_at ? "ok" : "warn"}>{sc.confirmed_at ? `recorded from ${sc.source} · confirmed` : `recorded from ${sc.source} — unconfirmed`}</Pill>}
          {sc.projection === "author" && packet.data?.projection === "own" && can("panel.ask_hiring_manager") && <button className="btn" onClick={() => setAskDialog(true)}>Ask the Hiring Manager</button>}
          {sc.projection === "author" && packet.data?.projection === "own" && can("panel.ask_hiring_manager") && <button className="btn" onClick={() => setAskRecruiterDialog(true)}>Ask your Recruiter</button>}
          {/* AF-7: Print stays on the scorecard as well as the packet. */}
          {sc.projection === "author" && <button className="btn" disabled={print.isPending || !packet.data} aria-describedby={!packet.data ? "packet-guide-status" : undefined} onClick={() => void print.mutateAsync({ packet_id: pk, application_id: cand, blank: false }).then(setPrinted).catch(() => undefined)}>Print saved scorecard</button>}
          {sc.projection === "author" && packet.data?.projection === "own" && packet.data.decline_notice && can("panel.decline_round") && !decline.isSuccess && packet.data.panel_seat_status !== "declined" && sc.interview_status !== "held" && packet.data.round_kind !== "recruiter_screen" && packet.data.round_kind !== "hm_round" && (
            <button className="btn" onClick={() => setDeclineDialog(true)}>Reschedule / Replace</button>
          )}
          {sc.can["confirm_recorded"] && (
            <button className="btn btn--primary" disabled={confirm.isPending} onClick={() => sc.scorecard_id && void confirm.mutateAsync({ scorecard_id: sc.scorecard_id })}>
              This is what I wrote
            </button>
          )}
          {sc.can["record_for"] && !paper && (
            <button className="btn" onClick={() => { if (timer.current) window.clearTimeout(timer.current); setStatus("idle"); setPaper(true); }}>
              Record from paper
            </button>
          )}
          {sc.can["append_addendum"] && (
            <button className="btn" onClick={() => setAddDlg(true)}>
              Add an addendum
            </button>
          )}
          {sc.can["revise"] && !revising && <button className="btn" onClick={() => {
            const prior = sc.revision ?? {};
            const base = emptyDraft(sc.dimension_set);
            // AF-16: the private notes come forward with the revision, so a correction never silently drops them.
            const fields = ["recommendation", "reservation_text", "consider_other_role_answer", "consider_other_role_suggested", "consider_other_role_why", "justification", "manager_handoff", "coc_answer", "coc_concern_text", "coc_concern_about", "private_notes"];
            const other = (prior.consider_other_role ?? {}) as Record<string, unknown>;
            setD({ ...base, ...Object.fromEntries(fields.map((key) => [key, prior[key] ?? null])), consider_other_role_answer: other.answer as "yes" | "no" | null, consider_other_role_suggested: other.suggested_role as string | null, consider_other_role_why: other.why as string | null, ratings: mergeRatings(sc.dimension_set, sc.ratings as RatingInput[] | null), attested: false });
            setRevising(true);
          }}>Revise</button>}
          {revising && <button className="btn" onClick={() => guard.confirm(() => { clearLocal(); setRevising(false); setD(null); })}>Cancel revision</button>}

        </div>
      </div>
      {packet.error && <div id="packet-guide-status" className="callout callout--warn" role="status">Packet guide unavailable. Your scorecard remains available. Contact the Recruiter if you need the guide or a printed copy.</div>}
      {packet.isPending && <p id="packet-guide-status" role="status">Loading packet guide… Your scorecard is available below.</p>}
      {/* Tejesh 26 Oct: the assignment's facts are the header line above and its kickoff reminders lead the frame's one
          guidance card (Interview guidance), so the scorecard shows no second card and no second disclosure */}
      {asked && <Receipt>{asked}</Receipt>}
      <ReasonDialog title="Ask the Hiring Manager" label="Your question" confirm="Ask" open={askDialog} onClose={() => setAskDialog(false)} busy={ask.isPending} error={ask.error} onConfirm={question => void ask.mutateAsync({ requisition_id: sc.requisition_id, packet_id: pk, question }).then(() => { setAskDialog(false); setAsked(ASK_RECEIPT.hiring_manager); })} />
      <ReasonDialog title="Ask your Recruiter" label="Your question" confirm="Ask" open={askRecruiterDialog} onClose={() => setAskRecruiterDialog(false)} busy={ask.isPending} error={ask.error} onConfirm={question => void ask.mutateAsync({ requisition_id: sc.requisition_id, packet_id: pk, question, audience: "recruiter" }).then(() => { setAskRecruiterDialog(false); setAsked(ASK_RECEIPT.recruiter); })} />
      {packet.data?.decline_notice && <RescheduleReplaceDialog open={declineDialog} notice={packet.data.decline_notice} busy={decline.isPending} error={decline.error} onClose={() => setDeclineDialog(false)} onConfirm={(input) => void decline.mutateAsync({ requisition_id: sc.requisition_id, round_def_id: packet.data!.round_def_id, ...input }).then(() => setDeclineDialog(false))} />}
      {decline.isSuccess && <Receipt>You are no longer on this panel. The Recruiter and Hiring Manager have been told.</Receipt>}
      {packet.data && sc.projection === "author" && <ResumeContext interview={packet.data?.interviews.find((i) => i["application_id"] === sc.application_id)} packetVersion={packet.data?.version} projection={packet.data?.projection} />}
      {sc.projection !== "author" && <Banner error={null} />}
      {sc.projection === "sealed" && <Empty level={2} heading="Scorecard is sealed">Sealed until the reveal — ratings and the recommendation are not visible before the debrief.</Empty>}
      {sc.projection === "compile" && sc.status === "draft" && <p className="muted">Draft — not submitted; visible to you as the Recruiter.</p>}
      {recovery && <div className="banner banner--warn" role="status">
        Restore your unsaved changes from {fmtTime(recovery.savedAt)}?
        <button className="btn" onClick={() => {
          const next = { ...recovery.draft, ratings: mergeRatings(sc.dimension_set, recovery.draft.ratings) };
          setD(next); setVersion(recovery.expectedVersion); setRevising(recovery.mode === "revision"); setPaper(recovery.mode === "paper");
          latest.current = { ...latest.current, d: next, version: recovery.expectedVersion, revising: recovery.mode === "revision", paper: recovery.mode === "paper", autosaves: recovery.mode === "draft" && canDraft };
          if (recovery.paperMetadata) {
            paperMetadata.current = recovery.paperMetadata;
            setSource(recovery.paperMetadata.source); setSourceFile(recovery.paperMetadata.sourceFile); setSourceRef(recovery.paperMetadata.sourceRef); setHappened(recovery.paperMetadata.happened);
          }
          dirtyRef.current = true; setDirty(true); setRecovery(null); remember(next);
          if (recovery.mode === "draft") { setStatus("saving"); timer.current = window.setTimeout(() => void flushLatest.current(), 0); }
        }}>Restore</button>
        <button className="btn" onClick={clearLocal}>Discard</button>
      </div>}
      {status === "conflict" && d && (
        <div className="banner banner--warn">
          This packet was saved elsewhere{otherDraft.data?.draft_saved_at ? ` at ${fmtTime(otherDraft.data.draft_saved_at)}` : ""}. <button className="btn btn--sm" onClick={() => flush(d, (save.error?.fieldErrors ?? {})["expected_version"] ?? null)}>Keep mine</button> <button className="btn btn--sm" onClick={() => { clearLocal(); setD(null); void q.refetch(); setStatus("idle"); }}>Take theirs</button> <button className="btn btn--sm" onClick={() => setComparing(true)}>Compare</button>
        </div>
      )}
      <Dialog title="Compare scorecard drafts" open={comparing && status === "conflict"} onClose={() => setComparing(false)}>
        <p>Your edits remain unchanged until you choose Keep mine or Take theirs.</p>
        {otherDraft.isPending && <Skeleton rows={3} />}
        <Banner error={otherDraft.error} onRetry={() => void otherDraft.refetch()} />
        {d && otherDraft.data && <><p className="muted">Saved elsewhere {fmtDate(otherDraft.data.draft_saved_at, true)}</p><DraftComparison local={d} remote={{ ...emptyDraft(sc.dimension_set), ...(otherDraft.data.draft as Partial<Draft> | null) }} dimensions={sc.dimension_set} skillTitles={skillTitles} /></>}
      </Dialog>
      {/* a draft refusal belongs to the author's own drafting only: recording from paper never saves a draft, so a
          stale save.error from before the switch must not surface beside the record (UAT 02 finding 10) */}
      <Banner error={!paper && save.error && status !== "conflict" ? save.error : submit.error} />
      {(submit.data || sc.submitted_at) && <Receipt>Submitted {fmtDate(submit.data?.effective_at ?? sc.submitted_at, true)} · visible to the Recruiter only until the debrief. {(submit.data?.receipt_id ?? sc.receipt_id) && <span className="mono">Receipt #{submit.data?.receipt_id ?? sc.receipt_id} · </span>}<button className="btn btn--sm" disabled={print.isPending || !packet.data} aria-describedby={!packet.data ? "packet-guide-status" : undefined} onClick={() => void print.mutateAsync({ packet_id: pk, application_id: cand, blank: false }).then(setPrinted).catch(() => undefined)}>Print a copy</button></Receipt>}
      {!revise.data && readonlyRev && sc.current_revision_no > 1 && <Receipt>Revision {sc.current_revision_no} recorded {fmtDate(String(readonlyRev["attested_at"]), true)} · Receipt #{String(readonlyRev["receipt_id"] ?? "unavailable")}.</Receipt>}
      {revise.data && <Receipt>Revision {revise.data.revision_no} recorded {fmtDate(revise.data.effective_at, true)} · Receipt #{revise.data.receipt_id}.</Receipt>}
      {paper && <div className="banner banner--warn">Recording for {sc.author_name} from {source}: the row will read "recorded for {sc.author_name} by you — unconfirmed" until they confirm.</div>}
      {playbookOpen && frameDraft && (
        <McScope>
          <PlaybookScorecard
            open={playbookOpen}
            draft={frameDraft}
            onChange={(next) => change(next)}
            mode={frameMode}
            editable={frameMode !== "readonly" && editable}
            hideAttestation={paper}
            missing={frameMissing}
            onSubmit={frameMode !== "readonly" && canEdit ? onFrameSubmit : undefined}
            submitLabel={paper ? `Record for ${sc.author_name}` : revising ? "Submit revision" : undefined}
            submitBusy={submitting || submit.isPending || recordFor.isPending || revise.isPending}
            saveState={saveState}
            errors={fieldErrors}
            reminders={sc.projection === "author" ? packet.data?.reminders : undefined}
            notesRail={sc.projection === "author" ? (
              <NotesRail
                value={frameDraft.private_notes}
                disabled={!editable || frameMode === "readonly"}
                onChange={frameMode === "readonly" ? undefined : (next) => change({ private_notes: next })}
              />
            ) : undefined}
          />
        </McScope>
      )}
      <Banner error={print.error} />
      <SavedPrint title="Printable saved scorecard" value={printed} kind="packet" onClose={() => setPrinted(null)} />
      <ReasonDialog title="Revise scorecard" label="Why this assessment changed" confirm="Submit revision" open={revisionDialog} onClose={() => setRevisionDialog(false)} error={revise.error} busy={revise.isPending} onConfirm={(reason) => d && void revise.mutateAsync({ packet_id: pk, application_id: cand, draft: d, reason }).then(() => { clearLocal(); setRevisionDialog(false); setRevising(false); setD(null); })}><p>The previous assessment stays in the record. This submits a new revision under your name.</p></ReasonDialog>
      <Dialog title="Record from paper" open={paperDlg} onClose={() => setPaperDlg(false)}>
        <Field label="Source" required id="rf-src">
          <select id="rf-src" value={source} onChange={(e) => changePaper({ source: e.target.value as "paper" | "verbal" })}>
            <option value="paper">Paper — the returned PDF</option>
            <option value="verbal">Verbal</option>
          </select>
        </Field>
        {source === "paper" ? paperDlg && <FileField label="Returned PDF" required owner={{ owner_type: "requisition", owner_id: sc.requisition_id, requisition_id: sc.requisition_id, kind: "paper_scorecard" }} accept={[DOCUMENT_TYPES[0]!]} onChange={(file) => changePaper({ sourceFile: file?.file_id ?? null })} onBusy={setSourceBusy} /> : <Field label="Evidence — who said what, when" required id="rf-ref"><input id="rf-ref" value={sourceRef} onChange={(e) => changePaper({ sourceRef: e.target.value })} /></Field>}
        <Field label="The interview happened on" required id="rf-when">
          <input id="rf-when" type="datetime-local" max={businessLocalNow()} value={happened} onChange={(e) => changePaper({ happened: e.target.value })} />
        </Field>
        <p className="muted">Entered by you; the author is {sc.author_name}. The author's Today gets "This is what I wrote".</p>
        <Banner error={recordFor.error} />
        <div className="acts">
          <button className="btn" onClick={() => setPaperDlg(false)}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={(source === "paper" ? !sourceFile || sourceBusy : !sourceRef.trim()) || !happened || recordFor.isPending || !d} onClick={() => d && void recordFor.mutateAsync({ interview_id: sc.interview_id, for_person: sc.author_id, source, ...(source === "paper" ? { source_file_id: sourceFile } : { evidence_ref: sourceRef }), happened_at: businessLocalToISO(happened), draft: submitBody(d, "paper") }).then(() => { clearLocal(); setPaperDlg(false); setPaper(false); })}>
            Record
          </button>
        </div>
      </Dialog>
      <ReasonDialog title="Add an addendum" label="Why" confirm="Append" open={addDlg} onClose={() => setAddDlg(false)} busy={addendum.isPending} error={addendum.error} onConfirm={(reason) => sc.scorecard_id && void addendum.mutateAsync({ scorecard_id: sc.scorecard_id, text: addText, reason }).then(() => { setAddDlg(false); setAddText(""); })}>
        <Field label="Addendum" required id="ad-text" hint="Prints beneath the original; changes no rating, recommendation or average">
          <textarea id="ad-text" rows={4} value={addText} onChange={(e) => setAddText(e.target.value)} />
        </Field>
      </ReasonDialog>
      {sc.addenda.length > 0 && (
        <section className="card">
          <h2 className="sec">Addenda</h2>
          <ul className="history">
            {sc.addenda.map((ad) => (
              <li key={String(ad["id"])}>
                <span className="muted">{fmtDate(String(ad["at"]), true)} · {String(ad["reason"])}</span>
                <div>{String(ad["text"])}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The card's dimensions in the frame's shape. */
function playbookDims(sc: { dimension_set: Record<string, unknown>[] }) {
  return sc.dimension_set.flatMap((dim) => (dim["type"] === "value" || dim["type"] === "skill") ? [{ type: dim["type"] as "value" | "skill", key: String(dim["key"]) }] : []);
}
